import {
  AlertTriangle,
  ArrowRight,
  Camera,
  CheckCircle2,
  Clock,
  Download,
  Info,
  Loader2,
  Target,
  Trash2,
  TrendingDown,
  TrendingUp,
  X,
} from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { toast } from "sonner";
import type { ForecastSnapshotResponseApi } from "@/api/models/forecastSnapshot";
import type {
  CategoryVarianceResponseApi,
  VarianceAnalysisResponseApi,
  VarianceDataPointResponseApi,
} from "@/api/models/varianceAnalysis";
import { PermissionGate } from "@/components/permissions/PermissionGate";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { useApp } from "@/contexts/AppContext";
import { useFormatting } from "@/hooks/useFormatting";
import { usePermissions } from "@/hooks/usePermissions";
import { useTerminology } from "@/hooks/useTerminology";
import {
  useCreateForecastSnapshot,
  useDeleteForecastSnapshot,
  useForecastSnapshots,
  useVarianceAnalysis,
} from "@/hooks/useVarianceAnalysis";
import { parseDateOnly } from "@/lib/forecast-dates";
import { getCurrencySymbol } from "@/utils/currency";

type Period = "7" | "30" | "90";

const FORECAST_PERIOD_OPTIONS = [
  { key: "90d", label: "90 Days" },
  { key: "6m", label: "6 Months" },
  { key: "12m", label: "12 Months" },
  { key: "3y", label: "3 Years" },
];

function mapVariancePeriodToForecastKey(period: string): string {
  switch (period) {
    case "7":
      return "90d";
    case "30":
      return "90d";
    case "90":
      return "90d";
    default:
      return "90d";
  }
}

function formatSnapshotDate(isoString: string): string {
  return new Date(isoString).toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

function getVarianceStatus(
  variance: number,
  categoryType: string,
): "favourable" | "adverse" | "on-track" {
  if (Math.abs(variance) < 100) return "on-track";
  if (categoryType === "inflow") {
    return variance > 0 ? "favourable" : "adverse";
  }
  return variance > 0 ? "adverse" : "favourable";
}

function exportCategoriesCsv(categories: CategoryVarianceResponseApi[]) {
  const header =
    "Category,Type,Actual,Forecasted,Budget,Forecast Variance,Budget Variance,Accuracy %";
  const rows = categories.map((c) =>
    [
      c.categoryName,
      c.categoryType,
      c.actualAmount,
      c.forecastedAmount,
      c.budgetAmount ?? "",
      c.forecastVariance,
      c.budgetVariance ?? "",
      c.forecastAccuracyPercent,
    ].join(","),
  );
  const csv = [header, ...rows].join("\n");
  const blob = new Blob([csv], { type: "text/csv" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = "variance-analysis.csv";
  a.click();
  URL.revokeObjectURL(url);
}

function ChartTooltip({
  active,
  payload,
  label,
}: {
  active?: boolean;
  payload?: {
    dataKey: string;
    value: number;
    payload?: { dateRange: string; week: string };
  }[];
  label?: string;
}) {
  const { formatCurrency } = useFormatting();
  const { getForecastTerm, getVarianceTerm } = useTerminology();
  if (!active || !payload?.length) return null;
  const forecasted =
    payload.find((p) => p.dataKey === "forecasted")?.value ?? 0;
  const actual = payload.find((p) => p.dataKey === "actual")?.value ?? 0;
  const variance = actual - forecasted;
  const dateRange = payload[0]?.payload?.dateRange;
  const weekLabel = payload[0]?.payload?.week;
  return (
    <div className="bg-background border rounded-lg shadow-lg p-3 text-sm">
      <p className="font-semibold mb-0.5">{weekLabel ?? label}</p>
      {dateRange && (
        <p className="text-xs text-muted-foreground mb-2">{dateRange}</p>
      )}
      <div className="space-y-1">
        <div className="flex gap-4 justify-between">
          <span className="text-muted-foreground">
            {getForecastTerm(true)}:
          </span>
          <span className="font-medium">{formatCurrency(forecasted)}</span>
        </div>
        <div className="flex gap-4 justify-between">
          <span className="text-muted-foreground">Actual:</span>
          <span className="font-medium">{formatCurrency(actual)}</span>
        </div>
        <div
          className={`flex gap-4 justify-between border-t pt-1 mt-1 ${variance >= 0 ? "text-success-600" : "text-destructive"}`}
        >
          <span>{getVarianceTerm(true)}:</span>
          <span className="font-semibold">{formatCurrency(variance)}</span>
        </div>
      </div>
    </div>
  );
}

function StatusBadge({
  status,
}: {
  status: "favourable" | "adverse" | "on-track";
}) {
  if (status === "favourable")
    return (
      <Badge className="bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400 text-xs">
        Favourable
      </Badge>
    );
  if (status === "adverse")
    return (
      <Badge className="bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400 text-xs">
        Adverse
      </Badge>
    );
  return (
    <Badge className="bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400 text-xs">
      On Track
    </Badge>
  );
}

function CategoryRow({
  item,
  formatCurrency,
  hasBudgetData,
}: {
  item: CategoryVarianceResponseApi;
  formatCurrency: (n: number) => string;
  hasBudgetData: boolean;
}) {
  const { getForecastTerm, getVarianceTerm } = useTerminology();
  const status = getVarianceStatus(item.forecastVariance, item.categoryType);

  const accentClass =
    status === "favourable"
      ? "border-l-4 border-l-success-500"
      : status === "adverse"
        ? "border-l-4 border-l-destructive"
        : "border-l-4 border-l-slate-300 dark:border-l-slate-600";

  return (
    <div className={`border rounded-lg overflow-hidden ${accentClass}`}>
      <div className="p-4">
        {/* Title row */}
        <div className="flex items-center gap-3 mb-3">
          <span className="font-semibold text-base leading-tight">
            {item.categoryName}
          </span>
          <StatusBadge status={status} />
          <Badge variant="outline" className="text-xs">
            {item.categoryType}
          </Badge>
        </div>

        {/* Data row */}
        <div
          className={`grid gap-6 ${hasBudgetData ? "grid-cols-5" : "grid-cols-4"}`}
        >
          <div>
            <span className="text-xs text-muted-foreground block mb-0.5">
              {getForecastTerm(true)}
            </span>
            <span className="text-sm font-semibold">
              {formatCurrency(item.forecastedAmount)}
            </span>
          </div>
          <div>
            <span className="text-xs text-muted-foreground block mb-0.5">
              Actual
            </span>
            <span className="text-sm font-semibold">
              {formatCurrency(item.actualAmount)}
            </span>
          </div>
          <div>
            <span className="text-xs text-muted-foreground block mb-0.5">
              {getVarianceTerm(true)}
            </span>
            <span
              className={`text-sm font-semibold ${status === "favourable" ? "text-success-600" : status === "adverse" ? "text-destructive" : "text-muted-foreground"}`}
            >
              {formatCurrency(item.forecastVariance)}
            </span>
          </div>
          {hasBudgetData && item.budgetAmount !== null && (
            <div>
              <span className="text-xs text-muted-foreground block mb-0.5">
                Budget
              </span>
              <span className="text-sm font-semibold">
                {formatCurrency(item.budgetAmount)}
                {item.budgetVariance !== null && (
                  <span
                    className={`ml-1 text-xs ${item.budgetVariance >= 0 ? "text-success-600" : "text-destructive"}`}
                  >
                    ({formatCurrency(item.budgetVariance)})
                  </span>
                )}
              </span>
            </div>
          )}
          <div>
            <span className="text-xs text-muted-foreground block mb-0.5">
              Accuracy
            </span>
            <span className="text-sm font-semibold">
              {item.forecastAccuracyPercent.toFixed(1)}%
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}

interface TakeSnapshotDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (
    name: string,
    description: string,
    forecastPeriod: string,
  ) => void;
  saving: boolean;
  defaultPeriod: string;
}

function TakeSnapshotDialog({
  open,
  onClose,
  onConfirm,
  saving,
  defaultPeriod,
}: TakeSnapshotDialogProps) {
  const { getForecastTerm, getVarianceTerm } = useTerminology();
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [forecastPeriod, setForecastPeriod] = useState(defaultPeriod);

  useEffect(() => {
    setForecastPeriod(defaultPeriod);
  }, [defaultPeriod]);

  function handleConfirm() {
    if (!name.trim()) return;
    onConfirm(name.trim(), description.trim(), forecastPeriod);
  }

  function handleClose() {
    setName("");
    setDescription("");
    setForecastPeriod(defaultPeriod);
    onClose();
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (!o) handleClose();
      }}
    >
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Camera className="h-4 w-4" />
            Take {getForecastTerm(true)} Snapshot
          </DialogTitle>
          <DialogDescription>
            Freeze the current {getForecastTerm(false).toLowerCase()} as a
            point-in-time baseline for future {getVarianceTerm(false)}{" "}
            comparisons.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="space-y-1.5">
            <Label htmlFor="snapshot-name">
              Snapshot name <span className="text-destructive">*</span>
            </Label>
            <Input
              id="snapshot-name"
              placeholder='e.g. "Q1 Close", "Pre-budget baseline"'
              value={name}
              onChange={(e) => setName(e.target.value)}
              disabled={saving}
              autoFocus
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="snapshot-description">
              Description{" "}
              <span className="text-muted-foreground text-xs">(optional)</span>
            </Label>
            <Textarea
              id="snapshot-description"
              placeholder="Brief note about why this snapshot was taken..."
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              disabled={saving}
              rows={3}
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="snapshot-period">Snapshot Period</Label>
            <Select
              value={forecastPeriod}
              onValueChange={setForecastPeriod}
              disabled={saving}
            >
              <SelectTrigger id="snapshot-period">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {FORECAST_PERIOD_OPTIONS.map((option) => (
                  <SelectItem key={option.key} value={option.key}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <p className="text-xs text-muted-foreground flex items-start gap-1.5">
            <Info className="h-3.5 w-3.5 flex-shrink-0 mt-0.5" />
            The snapshot captures today&apos;s{" "}
            {getForecastTerm(false).toLowerCase()} data. Once saved it cannot be
            edited &mdash; only archived.
          </p>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleClose} disabled={saving}>
            Cancel
          </Button>
          <Button onClick={handleConfirm} disabled={!name.trim() || saving}>
            <Camera className="h-4 w-4 mr-2" />
            {saving ? "Saving..." : `Take ${getForecastTerm(true)} Snapshot`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

interface SnapshotSelectorProps {
  snapshots: ForecastSnapshotResponseApi[];
  selectedId: number | null;
  onSelect: (id: number | null) => void;
  isLoading: boolean;
  analysisPeriodDays: number;
}

function getSnapshotCoverageDays(
  snapshot: ForecastSnapshotResponseApi,
): number {
  if (!snapshot.periodStart || !snapshot.periodEnd) return 0;
  const start = new Date(snapshot.periodStart).getTime();
  const end = new Date(snapshot.periodEnd).getTime();
  return Math.round((end - start) / (1000 * 60 * 60 * 24));
}

function SnapshotSelector({
  snapshots,
  selectedId,
  onSelect,
  isLoading,
  analysisPeriodDays,
}: SnapshotSelectorProps) {
  if (isLoading) {
    return (
      <div className="flex items-center gap-2 text-sm text-muted-foreground border rounded-md px-3 py-2 bg-muted/30">
        <Loader2 className="h-3.5 w-3.5 animate-spin" />
        <span>Loading snapshots...</span>
      </div>
    );
  }

  if (snapshots.length === 0) {
    return (
      <div className="flex items-center gap-2 text-sm text-muted-foreground border rounded-md px-3 py-2 bg-muted/30">
        <Camera className="h-3.5 w-3.5 flex-shrink-0" />
        <span>
          No snapshots saved yet &mdash; take one to enable comparison
        </span>
      </div>
    );
  }

  return (
    <div className="flex items-center gap-2 flex-wrap">
      <span className="text-sm text-muted-foreground whitespace-nowrap">
        Compare against:
      </span>
      <Select
        value={selectedId !== null ? String(selectedId) : "latest"}
        onValueChange={(v) => onSelect(v === "latest" ? null : Number(v))}
      >
        <SelectTrigger className="w-[280px]">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="latest">
            <span className="flex items-center gap-2">
              <span className="h-2 w-2 rounded-full bg-green-500 inline-block" />
              Latest Snapshot
              {snapshots[0] && (
                <span className="text-muted-foreground">
                  &mdash; {snapshots[0].name}
                </span>
              )}
            </span>
          </SelectItem>
          {snapshots.slice(1).map((s) => {
            const coverageDays = getSnapshotCoverageDays(s);
            const isPartial = coverageDays < analysisPeriodDays;
            return (
              <SelectItem
                key={s.forecastSnapshotId}
                value={String(s.forecastSnapshotId)}
              >
                <span className="flex items-center gap-2">
                  <Camera className="h-3 w-3 text-muted-foreground" />
                  {s.name}
                  {isPartial && (
                    <span className="text-xs text-amber-600 dark:text-amber-400">
                      Covers {coverageDays} of {analysisPeriodDays} days
                    </span>
                  )}
                </span>
              </SelectItem>
            );
          })}
        </SelectContent>
      </Select>

      {selectedId !== null &&
        (() => {
          const snap = snapshots.find(
            (s) => s.forecastSnapshotId === selectedId,
          );
          if (!snap) return null;
          const coverageDays = getSnapshotCoverageDays(snap);
          if (coverageDays >= analysisPeriodDays) return null;
          return (
            <div className="w-full flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 dark:bg-amber-950/20 dark:border-amber-800 px-3 py-2 mt-2">
              <AlertTriangle className="h-4 w-4 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
              <p className="text-xs text-amber-800 dark:text-amber-300">
                This snapshot only covers {coverageDays} of the{" "}
                {analysisPeriodDays} days in your selected period. Comparison
                values reflect partial data &mdash; they have not been scaled or
                extrapolated.
              </p>
            </div>
          );
        })()}
    </div>
  );
}

function NoSnapshotsState({ onTakeSnapshot }: { onTakeSnapshot: () => void }) {
  const { getForecastTerm, getVarianceTerm } = useTerminology();
  return (
    <Card className="border-dashed">
      <CardContent className="flex flex-col items-center justify-center py-16 text-center">
        <Camera className="h-12 w-12 text-muted-foreground/40 mb-4" />
        <h3 className="text-lg font-semibold mb-2">
          No {getForecastTerm(false)} snapshots available
        </h3>
        <p className="text-muted-foreground text-sm max-w-md mb-6">
          Create a {getForecastTerm(false)} snapshot to enable{" "}
          {getVarianceTerm(false)} analysis. Snapshots capture a point-in-time{" "}
          {getForecastTerm(false)} that can be compared against actual results.
        </p>
        <PermissionGate permission="manage_forecast_snapshots">
          <Button onClick={onTakeSnapshot}>
            <Camera className="h-4 w-4 mr-2" />
            Take {getForecastTerm(true)} Snapshot
          </Button>
        </PermissionGate>
      </CardContent>
    </Card>
  );
}

interface DeleteSnapshotDialogProps {
  open: boolean;
  snapshot: ForecastSnapshotResponseApi | null;
  onClose: () => void;
  onConfirm: (id: number) => void;
  deleting: boolean;
}

function DeleteSnapshotDialog({
  open,
  snapshot,
  onClose,
  onConfirm,
  deleting,
}: DeleteSnapshotDialogProps) {
  const { getVarianceTerm } = useTerminology();
  if (!snapshot) return null;

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (!o) onClose();
      }}
    >
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-destructive">
            <Trash2 className="h-4 w-4" />
            Archive Snapshot
          </DialogTitle>
          <DialogDescription>
            This action cannot be undone. The snapshot will be archived and
            removed from {getVarianceTerm(false)} analysis comparisons.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="rounded-lg border bg-muted/40 px-4 py-3 space-y-1">
            <p className="text-sm font-medium">{snapshot.name}</p>
            <p className="text-xs text-muted-foreground flex items-center gap-1.5">
              <Clock className="h-3 w-3" />
              Taken{" "}
              {new Date(snapshot.dateCreated).toLocaleDateString("en-GB", {
                day: "numeric",
                month: "short",
                year: "numeric",
              })}
            </p>
          </div>
          <div className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 dark:bg-amber-950/20 dark:border-amber-800 px-3 py-2">
            <AlertTriangle className="h-4 w-4 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-amber-800 dark:text-amber-300">
              Once archived, the point-in-time baseline this snapshot captured
              cannot be recreated. Any {getVarianceTerm(false)} analysis that
              relied on this snapshot will need to use a different baseline.
            </p>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={deleting}>
            Cancel
          </Button>
          <Button
            variant="destructive"
            onClick={() => onConfirm(snapshot.forecastSnapshotId)}
            disabled={deleting}
          >
            {deleting ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <Trash2 className="h-4 w-4 mr-2" />
            )}
            {deleting ? "Archiving..." : "Archive Snapshot"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Per-snapshot variance profiles — each snapshot tells a different story so
// switching the comparison baseline produces visibly different numbers in demo mode.
const DEMO_SNAPSHOT_PROFILES: Record<
  number,
  {
    inScaleForecast: number;
    inScaleActual: number;
    outScaleForecast: number;
    outScaleActual: number;
    accuracyOverall: number;
    label: string;
  }
> = {
  // "Q4 2025 Close" — strong period, inflows slightly ahead of forecast
  1: {
    inScaleForecast: 1.0,
    inScaleActual: 1.06,
    outScaleForecast: 1.0,
    outScaleActual: 0.97,
    accuracyOverall: 94.8,
    label: "Q4 2025 Close",
  },
  // "Pre-budget baseline" — cautious estimate, actuals came in softer on inflows
  2: {
    inScaleForecast: 1.12,
    inScaleActual: 1.0,
    outScaleForecast: 1.0,
    outScaleActual: 1.04,
    accuracyOverall: 88.3,
    label: "Pre-budget baseline",
  },
  // "Mid-year review" — broadly on track with minor outflow overspend
  3: {
    inScaleForecast: 1.0,
    inScaleActual: 1.01,
    outScaleForecast: 1.0,
    outScaleActual: 1.08,
    accuracyOverall: 91.2,
    label: "Mid-year review",
  },
};

function makeDemoVarianceData(
  periodDays: number,
  snapshotId: number | null,
): VarianceAnalysisResponseApi {
  const days = periodDays;
  const now = new Date();
  const actuals: VarianceDataPointResponseApi[] = [];
  const forecast: VarianceDataPointResponseApi[] = [];

  // Pick the profile for the selected snapshot — fall back to neutral if none selected
  const profile =
    snapshotId !== null
      ? (DEMO_SNAPSHOT_PROFILES[snapshotId] ?? DEMO_SNAPSHOT_PROFILES[1])
      : null;

  for (let i = days - 1; i >= 0; i--) {
    const d = new Date(now);
    d.setDate(d.getDate() - i);
    const dateStr = d.toISOString().split("T")[0] + "T00:00:00Z";
    const isPayDay = d.getDate() === 28;
    // Invoice days: every Monday that falls on a week boundary, plus mid-week smaller receipts
    const isLargeInvoiceDay = d.getDay() === 1 && i % 14 === 0;
    const isMidInvoiceDay = d.getDay() === 4 && i % 7 === 0;

    const baseForecastIn = isLargeInvoiceDay
      ? 52_000
      : isMidInvoiceDay
        ? 18_500
        : d.getDay() === 3
          ? 8_200
          : 3_100;
    const baseForecastOut = isPayDay
      ? 94_500
      : d.getDay() === 2
        ? 22_000
        : d.getDay() === 4
          ? 14_000
          : 5_800;

    const fInScale = profile?.inScaleForecast ?? 1.0;
    const aInScale = profile?.inScaleActual ?? 1.0;
    const fOutScale = profile?.outScaleForecast ?? 1.0;
    const aOutScale = profile?.outScaleActual ?? 1.0;

    const forecastIn = Math.round(baseForecastIn * fInScale);
    const forecastOut = Math.round(baseForecastOut * fOutScale);
    const actualIn = Math.round(baseForecastIn * aInScale);
    const actualOut = Math.round(baseForecastOut * aOutScale);

    actuals.push({
      date: dateStr,
      incoming: actualIn,
      outgoing: actualOut,
      net: actualIn - actualOut,
    });
    forecast.push({
      date: dateStr,
      incoming: forecastIn,
      outgoing: forecastOut,
      net: forecastIn - forecastOut,
    });
  }

  const totalActualIn = actuals.reduce((s, a) => s + a.incoming, 0);
  const totalActualOut = actuals.reduce((s, a) => s + a.outgoing, 0);
  const totalForecastIn = forecast.reduce((s, f) => s + f.incoming, 0);
  const totalForecastOut = forecast.reduce((s, f) => s + f.outgoing, 0);
  const totalVariance = totalActualIn - totalForecastIn;
  const overallAccuracy = profile?.accuracyOverall ?? 93.1;

  // Scale outflow category amounts with the outflow profile
  const aOut = profile?.outScaleActual ?? 1.0;
  const fOut = profile?.outScaleForecast ?? 1.0;

  // Scale inflow category amounts with the inflow profile
  const aIn = profile?.inScaleActual ?? 1.0;
  const fIn = profile?.inScaleForecast ?? 1.0;

  const basePayroll = Math.round(totalForecastOut * 0.38 * fOut);
  const actualPayroll = Math.round(totalForecastOut * 0.38 * aOut);

  const baseSubcontractors = Math.round(totalForecastOut * 0.28 * fOut);
  const actualSubcontractors = Math.round(
    totalForecastOut * 0.28 * aOut * 1.03,
  ); // slight overspend

  const baseMaterials = Math.round(totalForecastOut * 0.19 * fOut);
  const actualMaterials = Math.round(totalForecastOut * 0.19 * aOut * 1.05); // most adverse outflow

  const baseEquipment = Math.round(totalForecastOut * 0.09 * fOut);
  const actualEquipment = Math.round(totalForecastOut * 0.09 * aOut * 0.92); // favourable — deferred hire

  const baseOverheads = Math.round(totalForecastOut * 0.06 * fOut);
  const actualOverheads = Math.round(totalForecastOut * 0.06 * aOut * 0.98);

  const baseClientInvoices = Math.round(totalForecastIn * 0.68 * fIn);
  const actualClientInvoices = Math.round(totalForecastIn * 0.68 * aIn);

  const baseRetention = Math.round(totalForecastIn * 0.2 * fIn);
  const actualRetention = Math.round(totalForecastIn * 0.18 * aIn); // slightly short — late release

  const baseVariousIn = Math.round(totalForecastIn * 0.12 * fIn);
  const actualVariousIn = Math.round(totalForecastIn * 0.14 * aIn); // ahead — framework call-off

  return {
    actuals,
    forecast,
    budget: [],
    variance: [],
    categories: [
      // --- Inflows ---
      {
        categoryName: "Client Invoices",
        categoryType: "inflow",
        actualAmount: actualClientInvoices,
        forecastedAmount: baseClientInvoices,
        budgetAmount: null,
        forecastVariance: actualClientInvoices - baseClientInvoices,
        budgetVariance: null,
        forecastAccuracyPercent:
          Math.round(
            (Math.min(actualClientInvoices, baseClientInvoices) /
              Math.max(actualClientInvoices, baseClientInvoices)) *
              1000,
          ) / 10,
      },
      {
        categoryName: "Retention Releases",
        categoryType: "inflow",
        actualAmount: actualRetention,
        forecastedAmount: baseRetention,
        budgetAmount: null,
        forecastVariance: actualRetention - baseRetention,
        budgetVariance: null,
        forecastAccuracyPercent:
          Math.round(
            (Math.min(actualRetention, baseRetention) /
              Math.max(actualRetention, baseRetention)) *
              1000,
          ) / 10,
      },
      {
        categoryName: "Framework & Variations",
        categoryType: "inflow",
        actualAmount: actualVariousIn,
        forecastedAmount: baseVariousIn,
        budgetAmount: null,
        forecastVariance: actualVariousIn - baseVariousIn,
        budgetVariance: null,
        forecastAccuracyPercent:
          Math.round(
            (Math.min(actualVariousIn, baseVariousIn) /
              Math.max(actualVariousIn, baseVariousIn)) *
              1000,
          ) / 10,
      },
      // --- Outflows ---
      {
        categoryName: "Payroll",
        categoryType: "outflow",
        actualAmount: actualPayroll,
        forecastedAmount: basePayroll,
        budgetAmount: null,
        forecastVariance: basePayroll - actualPayroll,
        budgetVariance: null,
        forecastAccuracyPercent:
          Math.round(
            (Math.min(actualPayroll, basePayroll) /
              Math.max(actualPayroll, basePayroll)) *
              1000,
          ) / 10,
      },
      {
        categoryName: "Subcontractors",
        categoryType: "outflow",
        actualAmount: actualSubcontractors,
        forecastedAmount: baseSubcontractors,
        budgetAmount: null,
        forecastVariance: baseSubcontractors - actualSubcontractors,
        budgetVariance: null,
        forecastAccuracyPercent:
          Math.round(
            (Math.min(actualSubcontractors, baseSubcontractors) /
              Math.max(actualSubcontractors, baseSubcontractors)) *
              1000,
          ) / 10,
      },
      {
        categoryName: "Materials & Supplies",
        categoryType: "outflow",
        actualAmount: actualMaterials,
        forecastedAmount: baseMaterials,
        budgetAmount: null,
        forecastVariance: baseMaterials - actualMaterials,
        budgetVariance: null,
        forecastAccuracyPercent:
          Math.round(
            (Math.min(actualMaterials, baseMaterials) /
              Math.max(actualMaterials, baseMaterials)) *
              1000,
          ) / 10,
      },
      {
        categoryName: "Plant & Equipment Hire",
        categoryType: "outflow",
        actualAmount: actualEquipment,
        forecastedAmount: baseEquipment,
        budgetAmount: null,
        forecastVariance: baseEquipment - actualEquipment,
        budgetVariance: null,
        forecastAccuracyPercent:
          Math.round(
            (Math.min(actualEquipment, baseEquipment) /
              Math.max(actualEquipment, baseEquipment)) *
              1000,
          ) / 10,
      },
      {
        categoryName: "Overheads & Office",
        categoryType: "outflow",
        actualAmount: actualOverheads,
        forecastedAmount: baseOverheads,
        budgetAmount: null,
        forecastVariance: baseOverheads - actualOverheads,
        budgetVariance: null,
        forecastAccuracyPercent:
          Math.round(
            (Math.min(actualOverheads, baseOverheads) /
              Math.max(actualOverheads, baseOverheads)) *
              1000,
          ) / 10,
      },
    ],
    snapshotInfo: null,
    forecastAccuracyPercent: overallAccuracy,
    totalVariance,
    hasBudgetData: false,
  };
}

const DEMO_SNAPSHOTS_SEED: ForecastSnapshotResponseApi[] = [
  {
    forecastSnapshotId: 1,
    name: "Q4 2025 Close",
    description: "End-of-quarter baseline before January invoicing cycle",
    triggerType: "manual",
    snapshotDate: "2025-12-31T17:00:00Z",
    forecastPeriod: "90d",
    periodStart: "2025-10-01",
    periodEnd: "2025-12-31",
    resolution: "daily",
    dataPointCount: 92,
    startingCash: 62_400,
    minCash: 45_000,
    minCashDate: "2025-11-15",
    endingCash: 235_400,
    avgMonthlyInflow: 161_667,
    avgMonthlyOutflow: 104_000,
    totalInflow: 485_000,
    totalOutflow: 312_000,
    netCashFlow: 173_000,
    coverageMonths: 3,
    confidenceScore: 0.87,
    createdByUserId: "2",
    createdByUserName: "David Chen",
    dateCreated: "2025-12-31T17:00:00Z",
  },
  {
    forecastSnapshotId: 2,
    name: "Pre-budget baseline",
    description: "Snapshot taken before 2026 budget adjustments",
    triggerType: "manual",
    snapshotDate: "2026-01-15T09:30:00Z",
    forecastPeriod: "90d",
    periodStart: "2026-01-01",
    periodEnd: "2026-03-31",
    resolution: "daily",
    dataPointCount: 90,
    startingCash: 235_400,
    minCash: 180_000,
    minCashDate: "2026-02-20",
    endingCash: 406_900,
    avgMonthlyInflow: 173_333,
    avgMonthlyOutflow: 116_167,
    totalInflow: 520_000,
    totalOutflow: 348_500,
    netCashFlow: 171_500,
    coverageMonths: 3,
    confidenceScore: 0.91,
    createdByUserId: "2",
    createdByUserName: "David Chen",
    dateCreated: "2026-01-15T09:30:00Z",
  },
  {
    forecastSnapshotId: 3,
    name: "Mid-year review",
    description: "Interim review point ahead of H2 planning cycle",
    triggerType: "manual",
    snapshotDate: "2026-06-30T16:00:00Z",
    forecastPeriod: "90d",
    periodStart: "2026-04-01",
    periodEnd: "2026-06-30",
    resolution: "daily",
    dataPointCount: 91,
    startingCash: 247_000,
    minCash: 198_000,
    minCashDate: "2026-05-28",
    endingCash: 389_500,
    avgMonthlyInflow: 178_000,
    avgMonthlyOutflow: 122_500,
    totalInflow: 534_000,
    totalOutflow: 367_500,
    netCashFlow: 166_500,
    coverageMonths: 3,
    confidenceScore: 0.89,
    createdByUserId: "4",
    createdByUserName: "Linda Kowalski",
    dateCreated: "2026-06-30T16:00:00Z",
  },
];

interface VarianceAnalysisPageProps {
  onNavigate?: (page: string) => void;
}

export function VarianceAnalysisPage({
  onNavigate,
}: VarianceAnalysisPageProps) {
  const { formatCurrency } = useFormatting();
  const { getCashMovementTerms, getForecastTerm, getVarianceTerm } =
    useTerminology();
  const { mode } = useApp();
  const [period, setPeriod] = useState<Period>("30");
  const [selectedSnapshotId, setSelectedSnapshotId] = useState<number | null>(
    null,
  );
  const [snapshotDialogOpen, setSnapshotDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [snapshotToDelete, setSnapshotToDelete] =
    useState<ForecastSnapshotResponseApi | null>(null);
  const [demoSnapshots, setDemoSnapshots] =
    useState<ForecastSnapshotResponseApi[]>(DEMO_SNAPSHOTS_SEED);
  const [statusFilter, setStatusFilter] = useState<
    "favourable" | "adverse" | "on-track" | null
  >(null);

  const { data: liveSnapshotsData, isLoading: liveSnapshotsLoading } =
    useForecastSnapshots(mode !== "demo");
  const snapshots =
    mode === "demo" ? demoSnapshots : (liveSnapshotsData?.items ?? []);
  const snapshotsLoading = mode === "demo" ? false : liveSnapshotsLoading;

  const forecastTerm = getForecastTerm(true);
  const varianceTerm = getVarianceTerm(true);
  const { in: incomingTerm, out: outgoingTerm } = getCashMovementTerms();

  const {
    data: liveVarianceData,
    isLoading: liveVarianceLoading,
    error: liveVarianceError,
  } = useVarianceAnalysis(Number(period), selectedSnapshotId ?? undefined);

  // In demo mode, bypass the live API entirely and use local seed data.
  // Pass selectedSnapshotId so switching snapshots produces visibly different numbers.
  const demoVarianceData = useMemo(
    () =>
      mode === "demo"
        ? makeDemoVarianceData(Number(period), selectedSnapshotId)
        : null,
    [mode, period, selectedSnapshotId],
  );
  const varianceData = mode === "demo" ? demoVarianceData : liveVarianceData;
  const varianceLoading = mode === "demo" ? false : liveVarianceLoading;
  const varianceError = mode === "demo" ? null : liveVarianceError;

  const createSnapshotMutation = useCreateForecastSnapshot();
  const deleteSnapshotMutation = useDeleteForecastSnapshot();
  const { hasPermission } = usePermissions();
  const canDeleteSnapshots = hasPermission("delete_forecast_snapshots");

  const handleDeleteSnapshot = (id: number) => {
    if (mode === "demo") {
      setDemoSnapshots((prev) =>
        prev.filter((s) => s.forecastSnapshotId !== id),
      );
      if (selectedSnapshotId === id) setSelectedSnapshotId(null);
      setDeleteDialogOpen(false);
      setSnapshotToDelete(null);
      toast.success("Snapshot archived", {
        description:
          "The snapshot has been archived and removed from comparisons.",
      });
      return;
    }
    deleteSnapshotMutation.mutate(id, {
      onSuccess: () => {
        if (selectedSnapshotId === id) setSelectedSnapshotId(null);
        setDeleteDialogOpen(false);
        setSnapshotToDelete(null);
        toast.success("Snapshot archived", {
          description:
            "The snapshot has been archived and removed from comparisons.",
        });
      },
      onError: () => {
        toast.error("Failed to archive snapshot", {
          description: "Please try again or contact support.",
        });
      },
    });
  };

  const totalVariance = varianceData?.totalVariance ?? 0;
  const accuracyPercent = varianceData?.forecastAccuracyPercent ?? null;
  const hasBudgetData = varianceData?.hasBudgetData ?? false;

  const overallStatus: "favourable" | "adverse" | "on-track" =
    Math.abs(totalVariance) < 500
      ? "on-track"
      : totalVariance > 0
        ? "favourable"
        : "adverse";

  const categories = varianceData?.categories ?? [];
  const adverseCount = categories.filter(
    (c) => getVarianceStatus(c.forecastVariance, c.categoryType) === "adverse",
  ).length;
  const favourableCount = categories.filter(
    (c) =>
      getVarianceStatus(c.forecastVariance, c.categoryType) === "favourable",
  ).length;

  const toggleFilter = (status: "favourable" | "adverse" | "on-track") => {
    setStatusFilter((prev) => (prev === status ? null : status));
  };

  const filteredCategories = statusFilter
    ? categories.filter(
        (c) =>
          getVarianceStatus(c.forecastVariance, c.categoryType) ===
          statusFilter,
      )
    : categories;

  const chartData = useMemo(() => {
    if (!varianceData) return [];
    const days = Number(period);

    const shortMonths = [
      "Jan",
      "Feb",
      "Mar",
      "Apr",
      "May",
      "Jun",
      "Jul",
      "Aug",
      "Sep",
      "Oct",
      "Nov",
      "Dec",
    ];
    const shortDays = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

    function formatShortDate(d: Date): string {
      return `${d.getDate()} ${shortMonths[d.getMonth()]}`;
    }

    // 7-day view: one bar per day — weekly bucketing produces only 1 bar which looks broken
    if (days === 7) {
      return varianceData.actuals.map((dp, i) => {
        const d = parseDateOnly(dp.date);
        const forecastDp = varianceData.forecast[i];
        return {
          week: `${shortDays[d.getDay()]} ${d.getDate()}`,
          dateRange: formatShortDate(d),
          forecasted: Math.round(forecastDp?.net ?? 0),
          actual: Math.round(dp.net),
        };
      });
    }

    const weeks = Math.ceil(days / 7);
    const buckets: {
      week: string;
      dateRange: string;
      forecasted: number;
      actual: number;
    }[] = [];

    for (let i = 0; i < weeks; i++) {
      const weekStart = new Date();
      weekStart.setDate(weekStart.getDate() - days + i * 7);
      const weekEnd = new Date();
      weekEnd.setDate(weekEnd.getDate() - days + (i + 1) * 7);
      // weekEnd label: last day of the bucket (inclusive)
      const weekEndLabel = new Date(weekEnd);
      weekEndLabel.setDate(weekEndLabel.getDate() - 1);

      let forecastNet = 0;
      let actualNet = 0;

      varianceData.forecast.forEach((dp) => {
        const d = parseDateOnly(dp.date);
        if (d >= weekStart && d < weekEnd) forecastNet += dp.net;
      });
      varianceData.actuals.forEach((dp) => {
        const d = parseDateOnly(dp.date);
        if (d >= weekStart && d < weekEnd) actualNet += dp.net;
      });

      const dateRange = `${formatShortDate(weekStart)} – ${formatShortDate(weekEndLabel)}`;

      buckets.push({
        week: `Wk ${i + 1}`,
        dateRange,
        forecasted: Math.round(forecastNet),
        actual: Math.round(actualNet),
      });
    }

    return buckets;
  }, [varianceData, period]);

  const totalActualIn = varianceData
    ? varianceData.actuals.reduce((s, a) => s + a.incoming, 0)
    : 0;
  const totalActualOut = varianceData
    ? varianceData.actuals.reduce((s, a) => s + a.outgoing, 0)
    : 0;
  const totalForecastIn = varianceData
    ? varianceData.forecast.reduce((s, f) => s + f.incoming, 0)
    : 0;
  const totalForecastOut = varianceData
    ? varianceData.forecast.reduce((s, f) => s + f.outgoing, 0)
    : 0;

  function handleTakeSnapshot(
    name: string,
    description: string,
    forecastPeriod: string,
  ) {
    createSnapshotMutation.mutate(
      { name, description: description || undefined, forecastPeriod },
      {
        onSuccess: () => {
          toast.success(`Snapshot "${name}" saved`);
          setSnapshotDialogOpen(false);
          if (mode === "demo") {
            setDemoSnapshots((prev) => [
              {
                forecastSnapshotId: Date.now(),
                name,
                description: description || null,
                triggerType: "manual",
                snapshotDate: new Date().toISOString(),
                forecastPeriod: forecastPeriod || "90d",
                periodStart: null,
                periodEnd: null,
                resolution: "daily",
                dataPointCount: 0,
                startingCash: null,
                minCash: null,
                minCashDate: null,
                endingCash: null,
                avgMonthlyInflow: null,
                avgMonthlyOutflow: null,
                totalInflow: null,
                totalOutflow: null,
                netCashFlow: null,
                coverageMonths: null,
                confidenceScore: null,
                createdByUserId: null,
                createdByUserName: "Demo User",
                dateCreated: new Date().toISOString(),
              },
              ...prev,
            ]);
          }
        },
        onError: (error: Error) => {
          toast.error(
            error.message || "Failed to save snapshot — please try again",
          );
        },
      },
    );
  }

  if (varianceLoading) {
    return (
      <div className="space-y-6 p-6">
        <div className="flex items-center justify-center py-20">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      </div>
    );
  }

  if (varianceError || !varianceData) {
    return (
      <div className="space-y-6 p-6">
        <div className="flex justify-end">
          <div className="flex items-center gap-2">
            <PermissionGate permission="manage_forecast_snapshots">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setSnapshotDialogOpen(true)}
              >
                <Camera className="h-4 w-4 mr-2" />
                Take {getForecastTerm(true)} Snapshot
              </Button>
            </PermissionGate>
          </div>
        </div>
        <NoSnapshotsState onTakeSnapshot={() => setSnapshotDialogOpen(true)} />
        <TakeSnapshotDialog
          open={snapshotDialogOpen}
          onClose={() => setSnapshotDialogOpen(false)}
          onConfirm={handleTakeSnapshot}
          saving={createSnapshotMutation.isPending}
          defaultPeriod="90d"
        />
      </div>
    );
  }

  if (snapshots.length === 0 && !snapshotsLoading) {
    return (
      <div className="space-y-6 p-6">
        <div className="flex justify-end">
          <div className="flex items-center gap-2">
            <PermissionGate permission="manage_forecast_snapshots">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setSnapshotDialogOpen(true)}
              >
                <Camera className="h-4 w-4 mr-2" />
                Take {getForecastTerm(true)} Snapshot
              </Button>
            </PermissionGate>
          </div>
        </div>
        <NoSnapshotsState onTakeSnapshot={() => setSnapshotDialogOpen(true)} />
        <TakeSnapshotDialog
          open={snapshotDialogOpen}
          onClose={() => setSnapshotDialogOpen(false)}
          onConfirm={handleTakeSnapshot}
          saving={createSnapshotMutation.isPending}
          defaultPeriod="90d"
        />
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6">
      <div className="flex justify-end flex-wrap gap-3">
        <div className="flex items-center gap-2 flex-wrap">
          <SnapshotSelector
            snapshots={snapshots}
            selectedId={selectedSnapshotId}
            onSelect={setSelectedSnapshotId}
            isLoading={snapshotsLoading}
            analysisPeriodDays={Number(period)}
          />

          <Select value={period} onValueChange={(v) => setPeriod(v as Period)}>
            <SelectTrigger className="w-[160px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="7">Last 7 Days</SelectItem>
              <SelectItem value="30">Last 30 Days</SelectItem>
              <SelectItem value="90">Last 90 Days</SelectItem>
            </SelectContent>
          </Select>

          <PermissionGate permission="manage_forecast_snapshots">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setSnapshotDialogOpen(true)}
            >
              <Camera className="h-4 w-4 mr-2" />
              Take {getForecastTerm(true)} Snapshot
            </Button>
          </PermissionGate>

          <Button
            variant="outline"
            size="sm"
            onClick={() => exportCategoriesCsv(categories)}
          >
            <Download className="h-4 w-4 mr-2" />
            Export
          </Button>
        </div>
      </div>

      {selectedSnapshotId !== null &&
        (() => {
          const snap = snapshots.find(
            (s) => s.forecastSnapshotId === selectedSnapshotId,
          );
          if (!snap) return null;
          return (
            <div className="flex justify-end">
              <div className="flex items-center gap-2 text-xs border rounded-full px-3 py-1 bg-muted/40 w-fit">
                <Camera className="h-3 w-3 text-muted-foreground flex-shrink-0" />
                <span className="font-medium">{snap.name}</span>
                <span className="text-muted-foreground">&middot;</span>
                <Clock className="h-3 w-3 text-muted-foreground flex-shrink-0" />
                <span className="text-muted-foreground whitespace-nowrap">
                  {formatSnapshotDate(snap.dateCreated)}
                </span>
                <span className="text-muted-foreground">&middot;</span>
                <span className="text-muted-foreground whitespace-nowrap">
                  {formatCurrency(snap.netCashFlow ?? 0)} net
                </span>
                <button
                  type="button"
                  onClick={() => setSelectedSnapshotId(null)}
                  className="ml-1 text-muted-foreground hover:text-destructive transition-colors flex-shrink-0"
                  title="Use latest snapshot"
                >
                  <X className="h-3 w-3" />
                </button>
                {canDeleteSnapshots && (
                  <button
                    type="button"
                    onClick={() => {
                      setSnapshotToDelete(snap);
                      setDeleteDialogOpen(true);
                    }}
                    className="text-muted-foreground hover:text-destructive transition-colors flex-shrink-0"
                    title="Archive this snapshot"
                  >
                    <Trash2 className="h-3 w-3" />
                  </button>
                )}
              </div>
            </div>
          );
        })()}

      {mode !== "demo" && varianceData.snapshotInfo && (
        <div className="flex items-center gap-3 rounded-lg border border-blue-200 bg-blue-50 dark:bg-blue-950/20 dark:border-blue-800 px-4 py-3">
          <Camera className="h-4 w-4 text-blue-600 dark:text-blue-400 flex-shrink-0" />
          <p className="text-sm text-blue-800 dark:text-blue-300">
            Comparing actuals against snapshot{" "}
            <span className="font-semibold">
              &quot;{varianceData.snapshotInfo.name}&quot;
            </span>{" "}
            taken on{" "}
            <span className="font-semibold">
              {formatSnapshotDate(varianceData.snapshotInfo.snapshotDate)}
            </span>{" "}
            ({varianceData.snapshotInfo.triggerType} &middot;{" "}
            {varianceData.snapshotInfo.forecastPeriod})
          </p>
          {selectedSnapshotId !== null && (
            <button
              type="button"
              onClick={() => setSelectedSnapshotId(null)}
              className="ml-auto text-blue-500 hover:text-blue-700 dark:hover:text-blue-200 transition-colors flex-shrink-0"
              title="Use latest snapshot"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>
      )}

      <div className="grid gap-4 md:grid-cols-4" data-tour="variance-overview">
        {/* Forecast Accuracy */}
        <Card
          style={{
            borderLeft: "3px solid var(--mc-success-border)",
          }}
        >
          <CardContent className="p-4 flex flex-col justify-between h-full">
            <div className="flex items-start justify-between gap-2">
              <span className="text-base font-medium text-neutral-800">
                {forecastTerm} Accuracy
              </span>
              <div
                className="h-9 w-9 rounded-lg flex items-center justify-center flex-shrink-0"
                style={{
                  backgroundColor: "var(--mc-success-bg)",
                }}
              >
                <Target
                  className="h-4 w-4"
                  style={{
                    color: "var(--mc-success-icon)",
                  }}
                />
              </div>
            </div>
            <div className="mt-2">
              <div className="text-2xl font-bold text-neutral-900 tracking-tight">
                {accuracyPercent !== null
                  ? `${accuracyPercent.toFixed(1)}%`
                  : "—"}
              </div>
              <div className="text-xs text-muted-foreground mt-1">
                {accuracyPercent === null
                  ? "No forecast data"
                  : accuracyPercent >= 97
                    ? "Excellent accuracy"
                    : accuracyPercent >= 90
                      ? "Good accuracy"
                      : "Needs review"}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Net Variance */}
        <Card
          style={{
            borderLeft: "3px solid var(--mc-error-border)",
          }}
        >
          <CardContent className="p-4 flex flex-col justify-between h-full">
            <div className="flex items-start justify-between gap-2">
              <span className="text-base font-medium text-neutral-800">
                Net {varianceTerm}
              </span>
              <div
                className="h-9 w-9 rounded-lg flex items-center justify-center flex-shrink-0"
                style={{
                  backgroundColor: "var(--mc-error-bg)",
                }}
              >
                <TrendingDown
                  className="h-4 w-4"
                  style={{ color: "var(--mc-error-icon)" }}
                />
              </div>
            </div>
            <div className="mt-2">
              <div className="text-2xl font-bold text-neutral-900 tracking-tight">
                {formatCurrency(totalVariance)}
              </div>
              <div className="text-xs text-muted-foreground mt-1">
                {overallStatus === "favourable"
                  ? "Favourable"
                  : overallStatus === "adverse"
                    ? "Adverse"
                    : "On track"}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Incoming vs Forecast */}
        <Card style={{ borderLeft: "3px solid var(--mc-success-border)" }}>
          <CardContent className="p-4 flex flex-col justify-between h-full">
            <div className="flex items-start justify-between gap-2">
              <span className="text-base font-medium text-neutral-800">
                {incomingTerm} vs {forecastTerm}
              </span>
              <div
                className="h-9 w-9 rounded-lg flex items-center justify-center flex-shrink-0"
                style={{ backgroundColor: "var(--mc-success-bg)" }}
              >
                <TrendingUp
                  className="h-4 w-4"
                  style={{ color: "var(--mc-success-icon)" }}
                />
              </div>
            </div>
            <div className="mt-2">
              <div className="text-2xl font-bold text-neutral-900 tracking-tight">
                {formatCurrency(totalActualIn)}
              </div>
              <div className="text-xs text-muted-foreground mt-1">
                {forecastTerm}:{" "}
                <span className="font-medium text-foreground">
                  {formatCurrency(totalForecastIn)}
                </span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Outgoing vs Forecast */}
        <Card style={{ borderLeft: "3px solid var(--mc-warning-border)" }}>
          <CardContent className="p-4 flex flex-col justify-between h-full">
            <div className="flex items-start justify-between gap-2">
              <span className="text-base font-medium text-neutral-800">
                {outgoingTerm} vs {forecastTerm}
              </span>
              <div
                className="h-9 w-9 rounded-lg flex items-center justify-center flex-shrink-0"
                style={{ backgroundColor: "var(--mc-warning-bg)" }}
              >
                <TrendingDown
                  className="h-4 w-4"
                  style={{ color: "var(--mc-warning-icon)" }}
                />
              </div>
            </div>
            <div className="mt-2">
              <div className="text-2xl font-bold text-neutral-900 tracking-tight">
                {formatCurrency(totalActualOut)}
              </div>
              <div className="text-xs text-muted-foreground mt-1">
                {forecastTerm}:{" "}
                <span className="font-medium text-foreground">
                  {formatCurrency(totalForecastOut)}
                </span>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <Tabs defaultValue="breakdown">
        <TabsList>
          <PermissionGate permission="view_variance_by_category">
            <TabsTrigger value="breakdown">Category Breakdown</TabsTrigger>
          </PermissionGate>
          <TabsTrigger value="chart">Weekly Chart</TabsTrigger>
          <TabsTrigger value="balance">Balance Reconciliation</TabsTrigger>
        </TabsList>

        <TabsContent
          value="breakdown"
          className="mt-4 space-y-3"
          data-tour="variance-breakdown"
        >
          {adverseCount > 0 && (
            <Card className="border-amber-200 bg-amber-50 dark:bg-amber-950/20 dark:border-amber-800">
              <CardContent className="pt-4 pb-4">
                <div className="flex items-start gap-3">
                  <AlertTriangle className="h-5 w-5 text-amber-600 mt-0.5 flex-shrink-0" />
                  <div className="flex-1">
                    <h3 className="font-medium text-amber-900 dark:text-amber-300">
                      {adverseCount} adverse variance
                      {adverseCount > 1 ? "s" : ""} require
                      {adverseCount === 1 ? "s" : ""} attention
                    </h3>
                    <p className="text-sm text-amber-700 dark:text-amber-400 mt-0.5">
                      {categories
                        .filter(
                          (c) =>
                            getVarianceStatus(
                              c.forecastVariance,
                              c.categoryType,
                            ) === "adverse",
                        )
                        .map((c) => c.categoryName)
                        .join(", ")}
                      {" \u2014 "}review the details below and consider creating
                      an action if follow-up is needed.
                    </p>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    className="border-amber-300 text-amber-700 hover:bg-amber-100 dark:text-amber-400 dark:border-amber-700 flex-shrink-0"
                    onClick={() => onNavigate?.("actions")}
                  >
                    Create Action
                    <ArrowRight className="h-3 w-3 ml-1" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <span>
              {statusFilter
                ? `${filteredCategories.length} of ${categories.length} categories`
                : `${categories.length} categories`}
            </span>
            {favourableCount > 0 && (
              <Badge
                className={`cursor-pointer select-none transition-opacity ${statusFilter === "favourable" ? "opacity-100 ring-2 ring-green-500" : statusFilter !== null ? "opacity-40" : "opacity-100"} bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400`}
                onClick={() => toggleFilter("favourable")}
              >
                {favourableCount} favourable
              </Badge>
            )}
            {adverseCount > 0 && (
              <Badge
                className={`cursor-pointer select-none transition-opacity ${statusFilter === "adverse" ? "opacity-100 ring-2 ring-red-500" : statusFilter !== null ? "opacity-40" : "opacity-100"} bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400`}
                onClick={() => toggleFilter("adverse")}
              >
                {adverseCount} adverse
              </Badge>
            )}
            {categories.filter(
              (c) =>
                getVarianceStatus(c.forecastVariance, c.categoryType) ===
                "on-track",
            ).length > 0 && (
              <Badge
                className={`cursor-pointer select-none transition-opacity ${statusFilter === "on-track" ? "opacity-100 ring-2 ring-slate-400" : statusFilter !== null ? "opacity-40" : "opacity-100"} bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400`}
                onClick={() => toggleFilter("on-track")}
              >
                {
                  categories.filter(
                    (c) =>
                      getVarianceStatus(c.forecastVariance, c.categoryType) ===
                      "on-track",
                  ).length
                }{" "}
                on track
              </Badge>
            )}
            {statusFilter !== null && (
              <button
                onClick={() => setStatusFilter(null)}
                className="text-xs text-muted-foreground underline hover:text-foreground"
              >
                Clear filter
              </button>
            )}
          </div>

          {[
            ...filteredCategories.filter(
              (c) =>
                getVarianceStatus(c.forecastVariance, c.categoryType) ===
                "adverse",
            ),
            ...filteredCategories.filter(
              (c) =>
                getVarianceStatus(c.forecastVariance, c.categoryType) ===
                "on-track",
            ),
            ...filteredCategories.filter(
              (c) =>
                getVarianceStatus(c.forecastVariance, c.categoryType) ===
                "favourable",
            ),
          ].map((item) => (
            <CategoryRow
              key={`${item.categoryName}-${item.categoryType}`}
              item={item}
              formatCurrency={formatCurrency}
              hasBudgetData={hasBudgetData}
            />
          ))}
        </TabsContent>

        <TabsContent value="chart" className="mt-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">
                Net Cash Flow &mdash; {forecastTerm} vs Actual
                {mode !== "demo" && varianceData.snapshotInfo && (
                  <span className="ml-2 text-xs font-normal text-muted-foreground">
                    (snapshot: {varianceData.snapshotInfo.name})
                  </span>
                )}
              </CardTitle>
              <CardDescription>
                Weekly net cash movement (Last {period} Days)
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={chartData}
                    margin={{
                      top: 5,
                      right: 20,
                      left: 10,
                      bottom: Number(period) <= 30 ? 10 : 5,
                    }}
                  >
                    <CartesianGrid
                      strokeDasharray="3 3"
                      className="stroke-muted"
                    />
                    <XAxis
                      dataKey={Number(period) <= 30 ? "dateRange" : "week"}
                      tick={{
                        fontSize: Number(period) <= 30 ? 10 : 12,
                      }}
                      angle={Number(period) <= 30 ? -35 : 0}
                      textAnchor={Number(period) <= 30 ? "end" : "middle"}
                      height={Number(period) <= 30 ? 55 : 40}
                    />
                    <YAxis
                      tick={{ fontSize: 12 }}
                      width={60}
                      tickFormatter={(v: number) =>
                        `${getCurrencySymbol()}${(v / 1000).toFixed(0)}k`
                      }
                    />
                    <Tooltip content={<ChartTooltip />} />
                    <Legend
                      content={() => (
                        <div
                          style={{
                            display: "flex",
                            justifyContent: "center",
                            gap: 24,
                            marginTop: 8,
                            fontSize: 13,
                          }}
                        >
                          <span
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: 6,
                            }}
                          >
                            <span
                              style={{
                                width: 12,
                                height: 12,
                                borderRadius: 2,
                                backgroundColor: "var(--foreground)",
                                display: "inline-block",
                              }}
                            />
                            <span style={{ color: "var(--foreground)" }}>
                              Actual
                            </span>
                          </span>
                          <span
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: 6,
                            }}
                          >
                            <span
                              style={{
                                width: 12,
                                height: 12,
                                borderRadius: 2,
                                backgroundColor: "hsl(var(--chart-gray))",
                                display: "inline-block",
                              }}
                            />
                            <span style={{ color: "var(--foreground)" }}>
                              {forecastTerm}
                            </span>
                          </span>
                        </div>
                      )}
                    />
                    <ReferenceLine y={0} stroke="hsl(var(--border))" />
                    <Bar
                      dataKey="forecasted"
                      name={forecastTerm}
                      fill="hsl(var(--chart-gray))"
                      radius={[2, 2, 0, 0]}
                    />
                    <Bar
                      dataKey="actual"
                      name="Actual"
                      fill="hsl(var(--foreground))"
                      radius={[2, 2, 0, 0]}
                    />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="balance" className="mt-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">
                Balance Reconciliation
                {mode !== "demo" && varianceData.snapshotInfo && (
                  <span className="ml-2 text-xs font-normal text-muted-foreground">
                    (snapshot: {varianceData.snapshotInfo.name})
                  </span>
                )}
              </CardTitle>
              <CardDescription>
                {mode === "demo" && selectedSnapshotId !== null
                  ? (() => {
                      const snap = demoSnapshots.find(
                        (s) => s.forecastSnapshotId === selectedSnapshotId,
                      );
                      return snap
                        ? `Compared against snapshot: ${snap.name}`
                        : "How forecasted and actual cash positions compare over the period";
                    })()
                  : "How forecasted and actual cash positions compare over the period"}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {/* Opening balance row — shown in demo when a snapshot is selected */}
                {mode === "demo" &&
                  selectedSnapshotId !== null &&
                  (() => {
                    const snap = demoSnapshots.find(
                      (s) => s.forecastSnapshotId === selectedSnapshotId,
                    );
                    if (!snap?.startingCash) return null;
                    return (
                      <div className="rounded-lg border bg-muted/40 px-4 py-3 space-y-1">
                        <p className="text-xs text-muted-foreground font-medium uppercase tracking-wide">
                          Snapshot opening position
                        </p>
                        <div className="flex justify-between text-sm font-semibold">
                          <span>Cash at snapshot date ({snap.name})</span>
                          <span>{formatCurrency(snap.startingCash)}</span>
                        </div>
                        {snap.snapshotDate && (
                          <p className="text-xs text-muted-foreground">
                            Taken {formatSnapshotDate(snap.snapshotDate)}
                          </p>
                        )}
                      </div>
                    );
                  })()}

                <div className="space-y-2">
                  <p className="text-xs text-muted-foreground font-medium uppercase tracking-wide">
                    {forecastTerm}
                  </p>
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">
                      + {incomingTerm}
                    </span>
                    <span className="text-success-600 font-medium">
                      +{formatCurrency(totalForecastIn)}
                    </span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">
                      &ndash; {outgoingTerm}
                    </span>
                    <span className="text-destructive font-medium">
                      &ndash;{formatCurrency(totalForecastOut)}
                    </span>
                  </div>
                  <div className="flex justify-between text-sm font-semibold border-t pt-2">
                    <span>Net ({forecastTerm})</span>
                    <span>
                      {formatCurrency(totalForecastIn - totalForecastOut)}
                    </span>
                  </div>
                </div>

                <div className="space-y-2 pt-2">
                  <p className="text-xs text-muted-foreground font-medium uppercase tracking-wide">
                    Actual
                  </p>
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">
                      + {incomingTerm}
                    </span>
                    <span className="text-success-600 font-medium">
                      +{formatCurrency(totalActualIn)}
                    </span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">
                      &ndash; {outgoingTerm}
                    </span>
                    <span className="text-destructive font-medium">
                      &ndash;{formatCurrency(totalActualOut)}
                    </span>
                  </div>
                  <div className="flex justify-between text-sm font-semibold border-t pt-2">
                    <span>Net (Actual)</span>
                    <span>
                      {formatCurrency(totalActualIn - totalActualOut)}
                    </span>
                  </div>
                </div>

                <div
                  className={`flex justify-between text-sm font-semibold border-t pt-3 ${overallStatus === "favourable" ? "text-success-600" : overallStatus === "adverse" ? "text-destructive" : "text-muted-foreground"}`}
                >
                  <span>Net {varianceTerm}</span>
                  <span>{formatCurrency(totalVariance)}</span>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      <TakeSnapshotDialog
        open={snapshotDialogOpen}
        onClose={() => setSnapshotDialogOpen(false)}
        onConfirm={handleTakeSnapshot}
        saving={createSnapshotMutation.isPending}
        defaultPeriod="90d"
      />

      <DeleteSnapshotDialog
        open={deleteDialogOpen}
        snapshot={snapshotToDelete}
        onClose={() => {
          setDeleteDialogOpen(false);
          setSnapshotToDelete(null);
        }}
        onConfirm={handleDeleteSnapshot}
        deleting={deleteSnapshotMutation.isPending}
      />
    </div>
  );
}
